export enum AnalysisStatus {
    IN_PROGRESS = 'IN-PROGRESS',
    COMPLETE = 'COMPLETE',
    ERROR = 'ERROR',
}

export enum FactorCompletionFieldType {
    STATUS = 'completion_status',
    FEEDBACK = 'completion_feedback',
    WGU_ASPECT_STATUS = 'wgu-aspect-status',
}

export enum NoticeType {
    UNSUPPORTED_FILE_TYPE = 'unsupported-file-type',
    UNREADABLE_FILE = 'unreadable-file',
    UNSUPPORTED_FILE_ELEMENT = 'unsupported-file-element',
}

export type CompletionStatus = 'complete' | 'incomplete';

export type FactorCompletionField =
    | {
          fieldTypeName: FactorCompletionFieldType.STATUS;
          value: CompletionStatus;
      }
    | {
          fieldTypeName: FactorCompletionFieldType.FEEDBACK;
          value: string;
      }
    | {
          fieldTypeName: FactorCompletionFieldType.WGU_ASPECT_STATUS;
          value: string;
      };

export class AnalysisResponse {
    artifactInfos: Artifact[];
    createdAt: string;
    externalAuthorId: string;
    externalSubmissionId: string;
    externalTaskId: string;
    factorCompletions: FactorCompletion[];
    id: string;
    notices: Notice[];
    reportTypes: string[];
    submissionBeginDates: string[];
    status: AnalysisStatus;
    factorLabel: string;

    constructor(data: Partial<AnalysisResponse> = {}) {
        this.createdAt = data.createdAt
            ? new Date(data.createdAt)
                  .toLocaleString('en-US', {
                      month: 'numeric',
                      day: 'numeric',
                      year: '2-digit',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                  })
                  .replace(',', '')
            : 'Unknown Date';
        this.artifactInfos = data.artifactInfos || [];
        this.externalAuthorId = data.externalAuthorId || '';
        this.externalSubmissionId = data.externalSubmissionId || '';
        this.externalTaskId = data.externalTaskId || '';
        this.factorCompletions = data.factorCompletions
            ? data.factorCompletions.map(fc =>
                  fc instanceof FactorCompletion ? fc : new FactorCompletion(fc)
              )
            : [];
        this.id = data.id || '';
        this.notices = data.notices
            ? data.notices.map(notice => (notice instanceof Notice ? notice : new Notice(notice)))
            : [];
        this.reportTypes = data.reportTypes || [];
        this.submissionBeginDates = data.submissionBeginDates
            ? data.submissionBeginDates.map(date => new Date(date).toDateString())
            : [];
        this.status = data.status || AnalysisStatus.IN_PROGRESS;
        this.factorLabel = data.factorLabel || '';
    }

    updateFactorDetails(details: TaskSummary) {
        this.factorLabel = details.name;
        this.factorCompletions.forEach(completion => {
            const index = details.factors.findIndex(factor => factor.id === completion.factorId);
            if (index !== -1) {
                completion.taskDetail = details.factors[index];
            }
        });
    }

    getValidFactors(): FactorCompletion[] {
        return this.factorCompletions
            .filter(factor => factor.isValidFactor());
    }

    getIncompleteValidFactors(): FactorCompletion[] {
        return this.getValidFactors().filter(factor => {
            const statusField = factor.fields.find(
                field => field.fieldTypeName === FactorCompletionFieldType.STATUS
            );
            return statusField?.value === 'incomplete';
        });
    }

    areAllValidFactorsIncomplete(): boolean {
        const validFactors = this.getValidFactors();

        if (validFactors.length === 0) {
            return false;
        }

        return validFactors.every(factor => {
            const statusField = factor.fields.find(
                field => field.fieldTypeName === FactorCompletionFieldType.STATUS
            );
            return statusField?.value === 'incomplete';
        });
    }

    areAllValidFactorsComplete(): boolean {
        const validFactors = this.factorCompletions.filter(
            factor => factor.isValidFactor());

        if (validFactors.length === 0) {
            return false;
        }

        return validFactors.every(factor => {
            const statusField = factor.fields.find(
                field => field.fieldTypeName === FactorCompletionFieldType.STATUS
            );
            return statusField?.value === 'complete';
        });
    }
}

export type Artifact = {
    externalFileId: string;
    externalId: string;
    id: string;
    mimeType: string;
};

export class FactorCompletion {
    factorId: string;
    id: string;
    fields: FactorCompletionField[];
    taskDetail?: TaskDetail;

    constructor(data: Partial<FactorCompletion> = {}) {
        this.id = data.id || '';
        this.factorId = data.factorId || '';
        this.fields = data.fields || [];
        this.taskDetail = data.taskDetail || undefined;
    }

    isValidFactor(): boolean {
        const aspectStatusField = this.fields.find(
            field => field.fieldTypeName === FactorCompletionFieldType.WGU_ASPECT_STATUS
        );
        return !aspectStatusField;
    }
}

export class Notice {
    artifactExternalId: string;
    id: string;
    message: string;
    type: NoticeType | string;

    constructor(data: Partial<Notice> = {}) {
        this.artifactExternalId = data.artifactExternalId || '';
        this.id = data.id || '';
        this.message = data.message || '';
        this.type = data.type || '';
    }

    isKnownNoticeType(): boolean {
        return Object.values(NoticeType).includes(this.type as NoticeType);
    }
}

export type TaskSummary = {
    factors: TaskDetail[];
    name: string;
    id: string;
};
export type TaskDetail = {
    description: string;
    name: string;
    id: string;
    qualitySpecs: QualitySpec[];
};

export type QualitySpec = {
    criteria: string;
    qualityLevelLabel: 'Not Evident' | 'Approaching Competence' | 'Competent';
};
